import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Floating WhatsApp support button visible on every user-facing page.
// Hidden on admin routes to avoid clashing with the admin UI.
export default function FloatingWhatsAppButton() {
  const location = useLocation();
  const [number, setNumber] = useState('');

  useEffect(() => {
    // Support number is exposed via public /api/settings; hardcoded fallback ensures
    // the button always works even if that request is slow or fails. Country code (91)
    // is mandatory for wa.me links to open the chat.
    const FALLBACK = '919081110022';
    const envNumber = (process.env.REACT_APP_SUPPORT_WHATSAPP || '').replace(/[^0-9]/g, '');
    setNumber(envNumber || FALLBACK);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && d.support_whatsapp) {
          let n = String(d.support_whatsapp).replace(/[^0-9]/g, '');
          // If admin saved a 10-digit number without country code, prepend 91.
          if (n.length === 10) n = '91' + n;
          if (n) setNumber(n);
        }
      })
      .catch(() => {});
  }, []);

  if (location.pathname.startsWith('/admin')) return null;
  if (!number) return null;

  const message = encodeURIComponent('નમસ્તે, મારે મદદની જરૂર છે.');
  // Use canonical https://wa.me/ deep link – works consistently on iOS, Android,
  // and desktop browsers. Opening in a new tab avoids replacing the current page
  // when the WhatsApp app is not installed.
  const href = `https://wa.me/${number}?text=${message}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp Support"
      data-testid="floating-whatsapp-btn"
      className="fixed bottom-4 right-4 md:bottom-5 md:right-5 z-[9999] group"
    >
      <span className="absolute inset-0 rounded-full bg-green-500 opacity-40 animate-ping"></span>
      <span className="relative flex items-center gap-1.5 md:gap-2 bg-green-500 hover:bg-green-600 text-white font-bold pl-2.5 pr-3 py-2.5 md:pl-2.5 md:pr-3.5 md:py-2.5 rounded-full shadow-2xl ring-2 ring-white/30 transition-all">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="w-5 h-5 md:w-[18px] md:h-[18px] fill-white shrink-0">
          <path d="M19.11 17.2c-.28-.14-1.63-.8-1.88-.9-.25-.09-.43-.14-.62.14-.19.28-.72.9-.88 1.08-.16.19-.32.21-.6.07-.28-.14-1.16-.43-2.2-1.37-.81-.72-1.36-1.62-1.52-1.9-.16-.28-.02-.43.12-.56.13-.13.28-.32.42-.48.14-.16.19-.28.28-.46.09-.18.05-.35-.02-.49-.07-.14-.62-1.5-.85-2.06-.22-.53-.45-.46-.62-.47l-.53-.01c-.18 0-.48.07-.73.35-.25.28-.96.94-.96 2.29 0 1.35.99 2.66 1.12 2.84.14.19 1.93 2.96 4.69 4.15.66.29 1.17.46 1.57.59.66.21 1.26.18 1.74.11.53-.08 1.63-.66 1.86-1.3.23-.64.23-1.19.16-1.3-.07-.11-.25-.18-.53-.32Z"/>
          <path d="M27.2 4.8A15.94 15.94 0 0 0 16 .3C7.34.3.3 7.34.3 16c0 2.83.74 5.6 2.16 8.03L.1 31.9l8.06-2.31a15.7 15.7 0 0 0 7.83 2H16c8.66 0 15.7-7.04 15.7-15.7 0-4.19-1.63-8.13-4.5-11.09Zm-11.2 24a13.06 13.06 0 0 1-6.66-1.82l-.48-.28-4.79 1.37 1.4-4.67-.31-.5A13.05 13.05 0 0 1 3 16c0-7.2 5.86-13.06 13.06-13.06a13.02 13.02 0 0 1 9.24 3.83 12.98 12.98 0 0 1 3.82 9.24c0 7.2-5.86 13.06-13.12 13.06Z"/>
        </svg>
        <span className="hidden sm:inline text-xs md:text-[11px] leading-none">મદદ (Help)</span>
      </span>
    </a>
  );
}
